import { CreateClaimDialog } from "./create-claim/CreateClaimDialog";
import { AppErrorBoundary } from "@/components/AppErrorBoundary";
import { ClaimsFolderBar } from "@/components/claims/folders/ClaimsFolderBar";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { ListFilter } from "lucide-react";
import type { ClaimPipeline } from "@/api/generated/client/models/ClaimPipeline";
import type { ClaimStatusTemplate } from "@/api/generated/client/models/ClaimStatusTemplate";
import type { AssignedUser } from "@/api/generated/client/models/AssignedUser";

interface ClaimsToolbarProps {
    onSearch: (term: string) => void;
    onFilterChange: (status: string) => void;
    statusFilter: string;
    pipelineFilter: string;
    onPipelineChange: (pipeline: string) => void;
    pipelines: Pick<ClaimPipeline, "id" | "name" | "color">[];
    statuses: Pick<ClaimStatusTemplate, "id" | "pipelineId" | "displayName" | "color">[];
    assigneeFilter: string;
    onAssigneeChange: (assignee: string) => void;
    assigneeOptions: AssignedUser[];
    selectedFolderId: number | "all";
    onFolderSelect: (id: number | "all") => void;
}

export function ClaimsToolbar(props: ClaimsToolbarProps) {
    return (
        <AppErrorBoundary>
            <ClaimsToolbarContent {...props} />
        </AppErrorBoundary>
    );
}

function ClaimsToolbarContent({
    onSearch,
    onFilterChange,
    statusFilter,
    pipelineFilter,
    onPipelineChange,
    pipelines,
    statuses,
    assigneeFilter,
    onAssigneeChange,
    assigneeOptions,
    selectedFolderId,
    onFolderSelect,
}: ClaimsToolbarProps) {
    const activeFilterCount = Number(pipelineFilter !== "all") + Number(statusFilter !== "all") + Number(assigneeFilter !== "all") + Number(selectedFolderId !== "all");
    const workflowStatuses = statuses.filter((status) => String(status.pipelineId) === pipelineFilter);

    return (
        <div className="flex items-center justify-between gap-3 pb-4 max-sm:flex-col max-sm:items-stretch">
            <div className="flex min-w-0 flex-1 items-center gap-2 max-sm:flex-col max-sm:items-stretch">
                <input
                    type="search"
                    placeholder="Search claims..."
                    className="flex h-10 w-full max-w-[360px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 max-sm:max-w-none"
                    onChange={(e) => onSearch(e.target.value)}
                />
                <div className="flex items-center gap-2 max-sm:w-full">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button type="button" variant="outline" className="gap-2 max-sm:flex-1">
                                <ListFilter className="h-4 w-4" />
                                Filters
                                {activeFilterCount > 0 && (
                                    <span className="rounded-full bg-muted px-1.5 text-xs">{activeFilterCount}</span>
                                )}
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-52">
                            <DropdownMenuSub>
                                <DropdownMenuSubTrigger>Workflow</DropdownMenuSubTrigger>
                                <DropdownMenuSubContent className="w-52 max-h-[calc(100dvh-2rem)] overflow-y-auto">
                                    <DropdownMenuRadioGroup value={pipelineFilter} onValueChange={onPipelineChange}>
                                        <DropdownMenuRadioItem value="all" onSelect={(event) => event.preventDefault()}>All workflows</DropdownMenuRadioItem>
                                        {pipelines.map((pipeline) => (
                                            <DropdownMenuRadioItem key={pipeline.id} value={String(pipeline.id)} onSelect={(event) => event.preventDefault()}>
                                                <span className="inline-flex items-center gap-2">
                                                    <span
                                                        className="h-2.5 w-2.5 rounded-full border"
                                                        style={{ backgroundColor: pipeline.color ?? "#94A3B8" }}
                                                    />
                                                    {pipeline.name}
                                                </span>
                                            </DropdownMenuRadioItem>
                                        ))}
                                    </DropdownMenuRadioGroup>
                                </DropdownMenuSubContent>
                            </DropdownMenuSub>
                            <DropdownMenuSub>
                                <DropdownMenuSubTrigger disabled={pipelineFilter === "all"} className="data-[disabled]:text-muted-foreground">
                                    <span className="flex min-w-0 flex-1 flex-col">
                                        <span>Status</span>
                                        {pipelineFilter === "all" && (
                                            <span className="text-xs">Select a workflow first</span>
                                        )}
                                    </span>
                                </DropdownMenuSubTrigger>
                                <DropdownMenuSubContent className="w-52 max-h-[calc(100dvh-2rem)] overflow-y-auto">
                                    <DropdownMenuRadioGroup value={statusFilter} onValueChange={onFilterChange}>
                                        <DropdownMenuRadioItem value="all" onSelect={(event) => event.preventDefault()}>All statuses</DropdownMenuRadioItem>
                                        {workflowStatuses.map((status) => (
                                            <DropdownMenuRadioItem key={status.id} value={String(status.id)} onSelect={(event) => event.preventDefault()}>
                                                <span className="inline-flex items-center gap-2">
                                                    <span
                                                        className="h-2.5 w-2.5 rounded-full border"
                                                        style={{ backgroundColor: status.color ?? "#94A3B8" }}
                                                    />
                                                    {status.displayName}
                                                </span>
                                            </DropdownMenuRadioItem>
                                        ))}
                                    </DropdownMenuRadioGroup>
                                </DropdownMenuSubContent>
                            </DropdownMenuSub>
                            <DropdownMenuSub>
                                <DropdownMenuSubTrigger>Assignee</DropdownMenuSubTrigger>
                                <DropdownMenuSubContent className="w-52 max-h-[calc(100dvh-2rem)] overflow-y-auto">
                                    <DropdownMenuRadioGroup value={assigneeFilter} onValueChange={onAssigneeChange}>
                                        <DropdownMenuRadioItem value="all" onSelect={(event) => event.preventDefault()}>All assignees</DropdownMenuRadioItem>
                                        {assigneeOptions.map((assignee) => (
                                            <DropdownMenuRadioItem key={assignee.id} value={String(assignee.id)} onSelect={(event) => event.preventDefault()}>
                                                {assignee.name || assignee.email}
                                            </DropdownMenuRadioItem>
                                        ))}
                                    </DropdownMenuRadioGroup>
                                </DropdownMenuSubContent>
                            </DropdownMenuSub>
                            <ClaimsFolderBar
                                selectedFolderId={selectedFolderId}
                                onSelect={onFolderSelect}
                                asSubmenu
                            />
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>
            <div className="contents max-sm:flex max-sm:w-full max-sm:justify-end">
                <CreateClaimDialog />
            </div>
        </div>
    );
}
